// The inspection record's BOM tab: (+) adds a part, ACTIONS → Edit Quantity
// sets a line's quantity. Peak, Aaron 2026-10-06 (inspection 2554): "Bom tab
// needs a plus to add parts".
//
// Legacy (ajax/aircraft_templates_bom.inc): the add picks a part from the parts
// catalogue and writes it at quantity 1, refusing a part already on the BOM
// ("<number> - <name> is already added."); the line's quantity is then edited
// in place. Here one dialog does both — the part (add only) and the quantity —
// in the chrome of the record's other link dialogs (LinkAircraftDialog, the
// aircraft configuration's TemplateLimitationDialog).
import React, { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import { useSnackbar } from "notistack";

import PartAutocomplete, { type PartOption } from "../../components/PartAutocomplete";
import { addInspectionBomPart, setInspectionBomQuantity } from "../inspections_tr";
import type { InspectionBomRow } from "../types";

interface Props {
  open: boolean;
  inspectionId: number;
  /** null = add a part; a line = edit that line's quantity. */
  editing: InspectionBomRow | null;
  onClose: () => void;
  onSaved: () => void;
}

const InspectionBomDialog: React.FC<Props> = ({ open, inspectionId, editing, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [part, setPart] = useState<PartOption | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPart(null);
    setError(null);
    setQuantity(editing ? String(Number(editing.quantity ?? 1)) : "1");
  }, [open, editing]);

  const submit = async () => {
    if (!editing && !part) {
      setError("Choose the part to add");
      return;
    }
    if (!(Number(quantity) > 0)) {
      setError("Quantity must be more than 0");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await setInspectionBomQuantity(inspectionId, editing.id, quantity);
        enqueueSnackbar("Quantity saved", { variant: "success" });
      } else {
        await addInspectionBomPart(inspectionId, part!.id, quantity);
        enqueueSnackbar("Part added", { variant: "success" });
      }
      onSaved();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const title = editing ? `Edit quantity — ${[editing.number, editing.name].filter(Boolean).join(" ")}` : "Add Part";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          {!editing && (
            <Grid item xs={12} md={9}>
              <PartAutocomplete
                value={part}
                onChange={(next) => {
                  setPart(next);
                  setError(null);
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={editing ? 6 : 3}>
            <TextField
              fullWidth
              type="number"
              label="Quantity"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                setError(null);
              }}
              inputProps={{ min: 0, step: "any" }}
            />
          </Grid>
          {error && (
            <Grid item xs={12} role="alert" sx={{ color: "error.main", fontSize: 14 }}>
              {error}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {editing ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InspectionBomDialog;
